import dayjs from "dayjs";
import type {
  ActivityLevelModel,
  EventDetailModel,
  LiveEventAreaModel,
} from "../types/LiveEvent";
import type { HeatmapFilterModel } from "../types/Filter";

export interface DashboardApiFilter {
  neighborhood_ids?: string[];
  category_ids?: string[];
  demographic_type_ids?: number[];
  date_from?: Date;
  date_to?: Date;
  quick_filter_days?: number;
  event_name?: string;
  eventStatus?: number;
  attendance_type?: string;
  merchant_name?: string;
}

export interface DashboardFilterPayload {
  filter: DashboardApiFilter;
}

/**
 * Maps UI filter state to the dashboard request payload.
 * Preserves the legacy contract keys 1:1 (backend endpoints are unverified).
 */
export const toDashboardFilterPayload = (
  filters: HeatmapFilterModel | null,
): DashboardFilterPayload => {
  const filter: DashboardApiFilter = {};

  if (!filters) return { filter };

  if (filters.neighborhoodIDs && filters.neighborhoodIDs.length > 0) {
    filter.neighborhood_ids = filters.neighborhoodIDs;
  }
  if (filters.categoryIDs && filters.categoryIDs.length > 0) {
    filter.category_ids = filters.categoryIDs;
  }
  if (
    filters.demographicTypeIDs &&
    filters.demographicTypeIDs.length > 0 &&
    !filters.demographicTypeIDs.includes("0")
  ) {
    filter.demographic_type_ids = filters.demographicTypeIDs.map((id) => parseInt(id));
  }
  if (filters.fromDate) {
    filter.date_from = filters.fromDate;
  }
  if (filters.toDate) {
    filter.date_to = dayjs(filters.toDate).endOf("day").toDate();
  }
  if (filters.quickFilterDays) {
    filter.quick_filter_days = filters.quickFilterDays;
  }
  if (filters.eventName && filters.eventName.trim() !== "") {
    filter.event_name = filters.eventName.trim();
  }
  if (filters.eventStatus !== undefined && filters.eventStatus !== null && filters.eventStatus > 0) {
    filter.eventStatus = filters.eventStatus;
  }
  if (filters.attendanceType && filters.attendanceType !== "all") {
    filter.attendance_type = filters.attendanceType;
  }
  if (filters.merchantName && filters.merchantName.trim() !== "") {
    filter.merchant_name = filters.merchantName.trim();
  }

  return { filter };
};

/**
 * Live-events payload: same as dashboard payload minus the date range
 * (legacy quirk preserved).
 */
export const toLiveEventsPayload = (
  filters: HeatmapFilterModel | null,
): DashboardFilterPayload => {
  const payload = toDashboardFilterPayload(filters);
  delete payload.filter.date_from;
  delete payload.filter.date_to;
  return payload;
};

export const toActivityLevels = (areas: LiveEventAreaModel[]): ActivityLevelModel[] =>
  areas.map((area) => ({
    organization_population_id: area.organization_population_id,
    area_name: area.area_name,
    total_attendees: area.total_attendees,
    avg_engagement_rate: area.avg_engagement_rate,
    total_events: area.total_events,
    population: area.population,
  }));

export const toEventDetails = (areas: LiveEventAreaModel[]): EventDetailModel[] =>
  areas
    .flatMap((area) =>
      area.live_events.map((event) => ({
        event_id: event.event_id,
        event_name: event.event_name,
        status: event.status,
        categories: event.categories,
        start_time: event.start_time,
        end_time: event.end_time,
        total_attendees: event.total_attendees,
        civic_points_users: event.civic_points_users,
        non_civic_points_users: event.non_civic_points_users,
        engagement_rate: event.engagement_rate,
        created_at: event.created_at,
      })),
    )
    .filter((event) => event.status !== "")
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

export const downloadBlob = (data: BlobPart, contentType: string, filename: string): void => {
  const blob = new Blob([data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const extractDownloadFilename = (
  contentDisposition: string | null,
  fallback: string,
): string =>
  contentDisposition?.split("filename=")[1]?.replace(/"/g, "") || fallback;
